import { useState, useEffect } from 'react';
import { updatePass } from '../../services/adminService';
import { useAppContext } from '../../context/AppContext';
import { DaysOfTheWeekTrans, formatDateLocalized } from '../../utils/translations';
import { finalPriceLabel } from '../../utils/currencyFormat';

export default function EditPassModal({
  pass,
  token,
  onClose,
  onSuccess,
  onDelete,
  onTogglePaid,
  deletingId,
  updatingPaidId,
}) {
  const { setToaster, language } = useAppContext();
  const daysTrans = DaysOfTheWeekTrans[language] || DaysOfTheWeekTrans.english;
  const formatDate = (d) => formatDateLocalized(d, daysTrans);

  const [expiresAt, setExpiresAt] = useState('');
  const [paused, setPaused] = useState(false);
  const [isAdminPass, setIsAdminPass] = useState(false);
  const [finalPrice, setFinalPrice] = useState('');
  const [classesIncluded, setClassesIncluded] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!pass) return;
    setPaused(pass.status === 'paused');
    setIsAdminPass(!!pass.isAdmin);
    setFinalPrice(pass.finalPrice != null ? String(pass.finalPrice) : '');
    setClassesIncluded(pass.classesIncluded != null ? String(pass.classesIncluded) : '');
    if (pass.expiresAt) {
      const d = new Date(pass.expiresAt);
      setExpiresAt(d.toISOString().slice(0, 16));
    } else {
      setExpiresAt('');
    }
  }, [pass]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!pass?._id || !token) return;
    setSubmitting(true);
    try {
      const payload = { paused, isAdmin: isAdminPass };
      payload.expiresAt = expiresAt ? new Date(expiresAt).toISOString() : null;
      const priceNum = finalPrice.trim() === '' ? undefined : Number(finalPrice);
      if (typeof priceNum === 'number' && !Number.isNaN(priceNum)) payload.finalPrice = priceNum;
      if (classesIncluded.trim() === '') {
        payload.classesIncluded = null;
      } else {
        const classesIncludedNum = Number(classesIncluded);
        if (Number.isNaN(classesIncludedNum) || classesIncludedNum < 0) {
          setToaster({ message: 'Classes included must be a non-negative number', type: 'error', show: true });
          setSubmitting(false);
          return;
        }
        if (classesIncludedNum < (pass.classesUsed ?? 0)) {
          setToaster({ message: `Classes included cannot be less than classes already used (${pass.classesUsed ?? 0})`, type: 'error', show: true });
          setSubmitting(false);
          return;
        }
        payload.classesIncluded = classesIncludedNum;
      }
      await updatePass(token, pass._id, payload);
      setToaster({ message: 'Pass updated successfully', type: 'success', show: true });
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Error updating pass:', err);
      setToaster({ message: 'Failed to update pass', type: 'error', show: true });
    } finally {
      setSubmitting(false);
    }
  };

  const handleTogglePaidClick = () => {
    if (typeof onTogglePaid !== 'function') return;
    onTogglePaid(pass);
  };

  const handleDeleteClick = () => {
    if (typeof onDelete !== 'function') return;
    onDelete(pass._id);
  };

  if (!pass) return null;

  const isDeleting = deletingId === pass._id;
  const isTogglingPaid = updatingPaidId === pass._id;
  const isPaid = pass.paid !== false;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[80] p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100">
          <h3 className="text-xl font-semibold text-gray-800">Edit pass</h3>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <span className="text-2xl leading-none">×</span>
          </button>
        </div>

        <div className="overflow-y-auto flex-1 min-h-0 px-6 py-4 space-y-5">
          {/* Pass info card */}
          <div className="rounded-xl bg-gray-50 border border-gray-100 p-4 space-y-2">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Pass ID</p>
            <p className="text-sm font-mono text-gray-600 break-all">{pass._id}</p>
            <div className="pt-2 border-t border-gray-100 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <div>
                <span className="text-gray-400 block text-xs">User</span>
                <span className="text-gray-800">{pass.userId?.name || pass.userId?.email || '—'}</span>
              </div>
              <div>
                <span className="text-gray-400 block text-xs">Pass type</span>
                <span className="text-gray-800">{pass.passTypeId?.displayName || pass.passTypeId?.name || '—'}</span>
              </div>
              <div>
                <span className="text-gray-400 block text-xs">Status</span>
                <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                  pass.status === 'active' ? 'bg-green-100 text-green-700' :
                  pass.status === 'paused' ? 'bg-blue-100 text-blue-700' :
                  pass.status === 'exhausted' ? 'bg-amber-100 text-amber-700' :
                  pass.status === 'expired' ? 'bg-gray-100 text-gray-600' : 'bg-gray-100 text-gray-600'
                }`}>
                  {pass.status}
                </span>
              </div>
              <div>
                <span className="text-gray-400 block text-xs">Classes</span>
                <span className="text-gray-800">{pass.classesUsed ?? 0} / {pass.classesIncluded != null ? pass.classesIncluded : '∞'}</span>
              </div>
              {pass.purchasedAt && (
                <div className="col-span-2">
                  <span className="text-gray-400 block text-xs">Purchased</span>
                  <span className="text-gray-800">{formatDate(pass.purchasedAt)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Editable: Final price & Test pass */}
          <div className="rounded-xl bg-gray-50 border border-gray-100 p-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {finalPriceLabel(pass.currency)}
              </label>
              <input
                type="number"
                min="0"
                step="1"
                value={finalPrice}
                onChange={(e) => setFinalPrice(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-brown/20 focus:border-brown"
                placeholder="e.g. 100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Classes included
              </label>
              <input
                type="number"
                min={pass.classesUsed ?? 0}
                step="1"
                value={classesIncluded}
                onChange={(e) => setClassesIncluded(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-brown/20 focus:border-brown"
                placeholder="Leave blank for unlimited"
              />
              <p className="mt-1 text-xs text-gray-400">
                {pass.classesUsed ?? 0} already used — leave blank for unlimited
              </p>
            </div>
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="edit-pass-is-admin"
                checked={isAdminPass}
                onChange={(e) => setIsAdminPass(e.target.checked)}
                className="rounded border-gray-300 text-brown focus:ring-brown w-4 h-4"
              />
              <label htmlFor="edit-pass-is-admin" className="text-sm font-medium text-gray-700">
                Test pass (isAdmin)
              </label>
            </div>
          </div>

          {/* Payment status */}
          {typeof onTogglePaid === 'function' && (
            <div className="flex items-center justify-between rounded-xl bg-gray-50 border border-gray-100 p-4">
              <span className="text-sm font-medium text-gray-700">Payment</span>
              <button
                type="button"
                onClick={handleTogglePaidClick}
                disabled={isTogglingPaid}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${
                  isPaid ? 'bg-green-100 text-green-800 hover:bg-green-200' : 'bg-amber-100 text-amber-800 hover:bg-amber-200'
                }`}
              >
                {isTogglingPaid ? '…' : (isPaid ? 'Paid' : 'Unpaid')}
              </button>
            </div>
          )}

          {/* Edit form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="rounded-xl bg-gray-50 border border-gray-100 p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Expiry date</label>
                <input
                  type="datetime-local"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-brown/20 focus:border-brown"
                />
              </div>

              {(!pass.isSubscription || pass.status === 'paused') && (
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="edit-pass-paused"
                    checked={paused}
                    onChange={(e) => setPaused(e.target.checked)}
                    disabled={pass.isSubscription && !paused}
                    className="rounded border-gray-300 text-brown focus:ring-brown disabled:opacity-70 w-4 h-4"
                  />
                  <label htmlFor="edit-pass-paused" className="text-sm font-medium text-gray-700">
                    {pass.isSubscription && pass.status === 'paused'
                      ? 'Unpause pass'
                      : 'Pause pass (user cannot use for booking until unpaused)'}
                  </label>
                </div>
              )}
            </div>

            <div className="flex gap-3 justify-center md:justify-end">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2.5 bg-brown text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50"
              >
                {submitting ? 'Saving…' : 'Save'}
              </button>
            </div>
          </form>

          {/* Delete */}
          {typeof onDelete === 'function' && (
            <div className="pt-2 border-t border-gray-100">
              <button
                type="button"
                onClick={handleDeleteClick}
                disabled={isDeleting}
                className="w-full py-2.5 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
              >
                {isDeleting ? 'Deleting…' : 'Delete pass'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
